import { Provider } from '@angular/core';
import { HTTP_INTERCEPTORS } from '@angular/common/http';

// Interceptors
import { AuthorizationInterceptor } from './common/authorization.interceptor';
import { HttpTimeoutInterceptor } from './common/http-timeout.interceptor';
import { VersionInterceptor } from './common/version.interceptor';

// Guards
import {
    BillingToolAccessGuard,
    ContractorsOnlyAccessGuard,
    DocumentCenterGuard,
    GuardAuthentication,
    GuardClassicCarsDataManager,
    GuardDisposition,
    GuardLogin,
    GuardMasterDataManager,
    GuardOrdering,
    GuardQualityAssurance,
    GuardQualityAssuranceReadonly,
    OrderReadAccessGuard,
    OrderWriteAccessGuard,
    RoleGuard,
} from './guards';
import { MainAuthGuard } from './guards/main-auth.guard';

// Services
import {
    AccountingService,
    ApiService,
    AppMessageService,
    AuthorizationInfoService,
    BootstrapService,
    BranchService,
    CacheHandlerService,
    ChannelService,
    CustomerIdAccessor,
    CustomerService,
    ErrorHandlerService,
    FormatService,
    GarbageCollectorService,
    HeadersService,
    HubInfoService,
    LocalizedValidatorFactory,
    LocalStorageAccessor,
    LocationViewService,
    LookupDataService,
    NotificationService,
    OrderIdAccessor,
    OrderInformationService,
    OrderStateService,
    PartnerIdAccessor,
    PartnerService,
    PreviewDocumentsService,
    roleAccessorDependencies,
    RouterService,
    RoutingSegmentService,
    StorageService,
    TokenHandlerService,
    TokenRefreshService,
    UserIdAccessor,
    UserImageService,
    UserService,
    ValidationErrorTextExtractor,
    ValidationService,
} from './services';
import { ProductIdAccessor } from './services/accessors/product-id.accessor';
import { ProductVersionIdAccessor } from './services/accessors/product-version-id.accessor';
import { RolePathAccessor } from './services/accessors/role-path.accessor';
import { EventQueueService } from './services/eventQueue.service';
import { ProductService } from './product';
import { InspectionService } from '../inspection/services/inspection.service';

/**
 * Provides all core module services, guards, and interceptors as standalone providers.
 * This replaces the deprecated CoreModule pattern with modern standalone Angular providers.
 *
 * Usage in AppConfig or AppModule:
 * ```
 * bootstrapApplication(AppComponent, {
 *   providers: [provideCore(), ...]
 * });
 * ```
 */
export function provideCore(): Provider[] {
    return [
        // HTTP Interceptors
        {
            provide: HTTP_INTERCEPTORS,
            useClass: VersionInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthorizationInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HttpTimeoutInterceptor,
            multi: true,
        },

        // Core Services - Singleton Services
        RouterService,
        RoutingSegmentService,
        UserService,
        HeadersService,
        LocalStorageAccessor,
        StorageService,
        CacheHandlerService,
        BranchService,
        CustomerService,
        OrderStateService,
        PartnerService,
        GarbageCollectorService,
        ApiService,
        NotificationService,
        AppMessageService,
        LookupDataService,
        ErrorHandlerService,
        OrderInformationService,
        AuthorizationInfoService,
        TokenHandlerService,
        TokenRefreshService,
        PreviewDocumentsService,
        ChannelService,
        HubInfoService,
        LocationViewService,
        FormatService,
        LocalizedValidatorFactory,
        ValidationErrorTextExtractor,
        UserImageService,
        EventQueueService,
        ValidationService,
        ProductService,
        BootstrapService,
        InspectionService,

        // Route Guards
        GuardAuthentication,
        GuardLogin,
        GuardOrdering,
        GuardDisposition,
        GuardQualityAssurance,
        GuardQualityAssuranceReadonly,
        GuardMasterDataManager,
        GuardClassicCarsDataManager,
        OrderReadAccessGuard,
        OrderWriteAccessGuard,
        RoleGuard,
        DocumentCenterGuard,
        BillingToolAccessGuard,
        ContractorsOnlyAccessGuard,
        MainAuthGuard,

        // Accessors
        CustomerIdAccessor,
        PartnerIdAccessor,
        OrderIdAccessor,
        UserIdAccessor,
        ProductIdAccessor,
        ProductVersionIdAccessor,
        RolePathAccessor,

        // Role accessor dependencies (provides the accessor chain)
        roleAccessorDependencies(),
    ];
}
